/* eslint-disable no-console */
/* eslint-disable max-len */
import {LeftOutlined, RightOutlined} from '@ant-design/icons';
import {Button, Col, Flex, Row} from 'antd';
import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';

import Asset_Manager from '../../../assets/assetManager.png';
import BRSR_logo from '../../../assets/BRSR.png';
import CBAM from '../../../assets/cbam.png';
import CDP from '../../../assets/cdp.png';
import ExploreSol from '../../../assets/ExploreSol.png';
import GRI_logo from '../../../assets/GRI.png';
import InfraIcon from '../../../assets/infraIcon.png';
import Investment_Icon from '../../../assets/investment.png';
import MySDGPri from '../../../assets/MySDGPri.png';
import NetZero from '../../../assets/NetZero.png';
import Product_Icon from '../../../assets/Product_Icon.png';
import SASB_logo from '../../../assets/SASB.png';
import StakeEngage from '../../../assets/StakeEngage.png';
import TCFD from '../../../assets/TCFDIcon.png';
import UpgradeButton from '../../../components/UpgradeButton/UpgradeButton';
import {allRoutes, Routes} from '../../../constants/Routes';
import {accessModule} from '../../../store/companyStore/companyDetailsStore';
import {yearSelect} from '../../../utils/FormUtils/yearSelect';
import {removeSlash} from '../../../utils/removeSlash';
import {
  shouldBlockReportAccess,
  shouldBlockModuleAccess,
  shouldBlockReadOnly,
  shouldBlockFreeOrReadOnly
} from '../../../utils/SubscriptionHelper';
import {handleIsComingSoonConditionally} from '../../esgManager/constants/commonFunctions';
import {frameworkRoute} from '../../esgManager/constants/frameworkRoutes';

import HeaderTitle from './HeaderTitle';
import ProductLogos from './ProductLogos';

const CARDS_PER_VIEW = 4;

interface ProductCarouselProps<T extends {id: number}> {
  tabKey: string;
  items: T[];
  renderItem: (item: T) => React.ReactNode;
}

function ProductCarousel<T extends {id: number}>({
  tabKey,
  items,
  renderItem
}: ProductCarouselProps<T>) {
  const [start, setStart] = useState(0);

  const lastPageStart =
    items.length <= CARDS_PER_VIEW
      ? 0
      : (Math.ceil(items.length / CARDS_PER_VIEW) - 1) * CARDS_PER_VIEW;

  useEffect(() => {
    setStart(0);
  }, [tabKey]);

  useEffect(() => {
    setStart((s) => Math.min(s, lastPageStart));
  }, [items.length, lastPageStart]);

  const showNav = items.length > CARDS_PER_VIEW;
  const visible = showNav ? items.slice(start, start + CARDS_PER_VIEW) : items;

  return (
    <div className="productCardsCarousel">
      {showNav && (
        <Button
          type="text"
          className="productCarouselNav productCarouselNav--left productCarouselNavBtn"
          icon={<LeftOutlined />}
          disabled={start === 0}
          onClick={() => {
            setStart((s) => Math.max(0, s - CARDS_PER_VIEW));
          }}
          aria-label="Previous products"
        />
      )}
      <Row gutter={[16, 16]} className="productCardsCarousel__row">
        {visible.map((item) => (
          <Col key={item.id} span={12}>
            {renderItem(item)}
          </Col>
        ))}
      </Row>
      {showNav && (
        <Button
          type="text"
          className="productCarouselNav productCarouselNav--right productCarouselNavBtn"
          icon={<RightOutlined />}
          disabled={start >= lastPageStart}
          onClick={() => {
            setStart((s) => Math.min(lastPageStart, s + CARDS_PER_VIEW));
          }}
          aria-label="Next products"
        />
      )}
    </div>
  );
}

interface BusinessItem {
  id: number;
  title: string;
  logo: string;
  description: string;
  redirect?: string;
  product?: string;
  mainRoute?: string | string[];
  isUpcoming?: boolean;
  href?: string;
  isBlock: boolean;
  esgRoutes?: frameworkRoute;
}

const ProductSection: React.FC<{
  setProduct: React.Dispatch<React.SetStateAction<string>>;
  setOpenKeys: React.Dispatch<React.SetStateAction<string[]>>;
}> = ({setProduct, setOpenKeys}) => {
  const [selectedCA, setSelectedCA] = useState(true);
  const [selectedESG, setSelectedESG] = useState(false);
  const currentYear = yearSelect()?.[0].value;
  const [selectedStrategy, setSelectedStrategy] = useState(false);
  const navigate = useNavigate();

  const [data, setData] = useState<BusinessItem[]>([
    {
      id: 1,
      title: 'Resource Utilization',
      logo: InfraIcon,
      description:
        'Track your energy consumption from utilities and vehicle usage for commute & transport.',
      redirect: `${allRoutes.OM.infraProfile}`,
      product: 'ca',
      mainRoute: removeSlash(Routes.ORG_MANAGER),
      isBlock: shouldBlockReadOnly()
    },
    {
      id: 2,
      title: 'Asset & Franchises',
      logo: Asset_Manager,
      description: 'Track emissions from your leased assets and Franchises.',
      redirect: `${allRoutes.OM.assetManagement}`,
      product: 'ca',
      mainRoute: removeSlash(Routes.ORG_MANAGER),
      isBlock: shouldBlockFreeOrReadOnly()
    },
    {
      id: 3,
      title: 'Investments',
      logo: Investment_Icon,
      description:
        'Investment is one of the 15 key categories in Scope 3 emissions, reflecting the environmental impact of financial activities.',
      redirect: `${allRoutes.OM.investments}`,
      product: 'ca',
      mainRoute: removeSlash(Routes.ORG_MANAGER),
      isBlock:
        shouldBlockModuleAccess(accessModule.scope3) || shouldBlockReadOnly()
    },
    {
      id: 4,
      title: 'Product Lifecycle',
      logo: Product_Icon,
      description:
        'Track the footprint of a product journey from raw material usage to production, use, and disposal.',
      redirect: `${allRoutes.OM.products}`,
      product: 'ca',
      mainRoute: removeSlash(Routes.ORG_MANAGER),
      isBlock:
        shouldBlockModuleAccess(accessModule.scope3) || shouldBlockReadOnly()
    }
  ]);

  const [ESGdata, setESGData] = useState<BusinessItem[]>([
    {
      id: 1,
      title: 'BRSR',
      esgRoutes: frameworkRoute.BRSR,
      logo: BRSR_logo,
      description:
        'A framework for Indian companies to disclose their environmental, social, and governance (ESG) performance.',
      redirect: `${allRoutes.ESG.FRAMEWORK}?selectedYear=${currentYear}`,
      product: 'esg',
      isBlock: shouldBlockReportAccess('BRSR') || shouldBlockReadOnly()
    },
    {
      id: 2,
      title: 'GRI',
      esgRoutes: frameworkRoute.GRI,
      logo: GRI_logo,
      description:
        'An international standard for sustainability reporting, helping organizations measure and communicate their impact on the environment, society, and economy.',
      redirect: `${allRoutes.ESG.FRAMEWORK}?selectedYear=${currentYear}`,
      product: 'esg',
      isBlock: shouldBlockReportAccess('GRI') || shouldBlockReadOnly()
    },
    {
      id: 3,
      title: 'SASB',
      logo: SASB_logo,
      esgRoutes: frameworkRoute.SASB,
      description:
        'A set of standards for disclosing material sustainability information to investors, focusing on financial performance and long-term value.',
      redirect: `${allRoutes.ESG.FRAMEWORK}?selectedYear=${currentYear}`,
      product: 'esg',
      isBlock: shouldBlockReportAccess('SASB') || shouldBlockReadOnly()
    },
    {
      id: 4,
      title: 'TCFD',
      esgRoutes: frameworkRoute.TCFD,
      logo: TCFD,
      description:
        'Guidelines for companies to disclose climate-related risks and opportunities in their financial reports to help investors make informed decisions.',
      redirect: `${allRoutes.ESG.FRAMEWORK}?selectedYear=${currentYear}`,
      product: 'esg',
      isBlock: shouldBlockReportAccess('TCFD') || shouldBlockReadOnly()
    },
    {
      id: 5,
      title: 'CBAM',
      esgRoutes: frameworkRoute.CBAM,
      logo: CBAM,
      description:
        'An EU mechanism that requires reporting and pricing of embedded carbon emissions in certain imported goods',
      redirect: `${allRoutes.ESG.FRAMEWORK}?selectedYear=${currentYear}`,
      product: 'esg',
      isBlock: shouldBlockReportAccess('CBAM') || shouldBlockReadOnly()
    },
    {
      id: 6,
      title: 'CDP Corporate',
      esgRoutes: frameworkRoute.CDP_CORPORATE,
      logo: CDP,
      description:
        'A global disclosure framework for companies to report their environmental impacts, risks, and climate actions.',
      redirect: `${allRoutes.ESG.FRAMEWORK}?selectedYear=${currentYear}`,
      product: 'esg',
      isBlock: shouldBlockReportAccess('CDP_CORPORATE') || shouldBlockReadOnly()
    },
    {
      id: 7,
      title: 'CDP SME',
      esgRoutes: frameworkRoute.CDP_SME,
      logo: CDP,
      description:
        'A simplified CDP framework that helps SMEs measure and disclose their environmental performance.',
      redirect: `${allRoutes.ESG.FRAMEWORK}?selectedYear=${currentYear}`,
      product: 'esg',
      isBlock: shouldBlockReportAccess('CDP_SME') || shouldBlockReadOnly()
    }
  ]);

  const [strategyData, setStrategyData] = useState<BusinessItem[]>([
    {
      id: 1,
      title: 'Stakeholder Engagement',
      logo: StakeEngage,
      description:
        'Involvement of stakeholders in decision-making processes to drive sustainability.',
      href:
        process.env.REACT_APP_STRATEGY_URL ??
        'https://dev-strategy.bharatcarbon.earth',
      isBlock:
        shouldBlockModuleAccess(
          accessModule.strategy,
          accessModule.pageAccessConfigs,
          accessModule.stakeHolderEngagement
        ) || shouldBlockReadOnly()
    },
    {
      id: 2,
      title: 'My SDG Priorities',
      logo: MySDGPri,
      description:
        'Identify and prioritize Sustainable Development Goals (SDGs) to align with organizational strategies for global impact.',
      href: process.env.REACT_APP_STRATEGY_URL
        ? `${process.env.REACT_APP_STRATEGY_URL}/SdgPriorities`
        : 'https://dev-strategy.bharatcarbon.earth/SdgPriorities',
      isBlock:
        shouldBlockModuleAccess(
          accessModule.strategy,
          accessModule.pageAccessConfigs,
          accessModule.mySDG
        ) || shouldBlockReadOnly()
    },
    {
      id: 3,
      title: 'My Net Zero Target',
      logo: NetZero,
      description:
        'Utilize standard SBTI targets or establish one to go net zero!',
      href: process.env.REACT_APP_STRATEGY_URL
        ? `${process.env.REACT_APP_STRATEGY_URL}/NetZeroTarget`
        : 'https://dev-strategy.bharatcarbon.earth/NetZeroTarget',
      isBlock:
        shouldBlockModuleAccess(
          accessModule.strategy,
          accessModule.pageAccessConfigs,
          accessModule.myNetZeroTarget
        ) || shouldBlockReadOnly()
    },
    {
      id: 4,
      title: 'Explore Solutions',
      logo: ExploreSol,
      description:
        'Explore the marketplace to discover suitable green projects that contribute to your decarbonization.',
      href: process.env.REACT_APP_STRATEGY_URL
        ? `${process.env.REACT_APP_STRATEGY_URL}/exploreSolution/ESGService`
        : 'https://dev-strategy.bharatcarbon.earth/exploreSolution/ESGService',
      isBlock:
        shouldBlockModuleAccess(
          accessModule.strategy,
          accessModule.pageAccessConfigs,
          accessModule.exploreSol
        ) || shouldBlockReadOnly()
    }
  ]);

  const productTitle = [
    {
      key: selectedCA,
      title:
        // eslint-disable-next-line @typescript-eslint/quotes, quotes
        "Measure and track your organization's greenhouse gas emissions to drive effective climate change."
    },
    {
      key: selectedESG,
      title:
        'Disclose environmental, social, and governance performance to ensure transparency and compliance.'
    },
    {
      key: selectedStrategy,
      title:
        'Develop a roadmap to reduce and offset emissions, achieving carbon neutrality.'
    }
  ];

  useEffect(() => {
    setData((prev) => [...prev]);
  }, []);

  useEffect(() => {
    setESGData(
      (prev) =>
        prev?.map((item) => {
          return {
            ...item,
            isUpcoming: handleIsComingSoonConditionally(item?.esgRoutes, false)
          };
        })
    );
  }, []);

  useEffect(() => {
    setStrategyData((prev) => [...prev]);
  }, []);

  const ProductCA = () => {
    setSelectedCA(true);
    setSelectedESG(false);
    setSelectedStrategy(false);
  };

  const ProductESG = () => {
    setSelectedCA(false);
    setSelectedESG(true);
    setSelectedStrategy(false);
  };

  const ProductStrategy = () => {
    setSelectedCA(false);
    setSelectedESG(false);
    setSelectedStrategy(true);
  };

  const styles = {
    display: 'flex',
    padding: '24px',
    alignItems: 'center',
    gap: '8px',
    borderRadius: '100px',
    margin: '10px',
    width: '100px',
    height: '100px',
    alignSelf: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
  };

  return (
    <div className="getStartedContainer">
      <HeaderTitle
        headText={'Products'}
        subText={''}
        hasOption={false}
        scrollNext={undefined}
        scrollPrev={undefined}
      />
      <div className="productContainer">
        {/* left div */}
        <div className="leftDiv">
          <div className="CADiv">
            <div
              style={{
                ...styles,
                background: selectedCA ? ' #17A086' : '',
                border: '1px solid #17a086'
              }}
              onClick={ProductCA}
            >
              <ProductLogos
                color={selectedCA ? '#ffffff' : '#17A086'}
                product="CA"
              />
            </div>
            <div className="circleVame">Carbon Accounting</div>
          </div>

          <div className="CADiv">
            <div
              style={{
                ...styles,
                background: selectedESG ? ' #719A5D' : '',
                border: '1px solid #719A5D'
              }}
              onClick={ProductESG}
            >
              <ProductLogos
                color={selectedESG ? '#ffffff' : '#719A5D'}
                product="ESG"
              />
            </div>
            <div className="circleVame">ESG Reporting</div>
          </div>

          <div className="CADiv">
            <div
              style={{
                ...styles,
                background: selectedStrategy ? ' #E67627' : '',
                border: '1px solid #E67627'
              }}
              onClick={ProductStrategy}
            >
              <ProductLogos
                color={selectedStrategy ? '#ffffff' : '#E67627'}
                product="Strategy"
              />
            </div>
            <div className="circleVame">Net Zero Strategy</div>
          </div>
        </div>

        {/* divider */}
        <div className="divider2"></div>

        <div className="rightDiv">
          {productTitle.map((item, index) => {
            return (
              item?.key && (
                <div className="productTitle" key={index}>
                  {item?.title}
                </div>
              )
            );
          })}
          {selectedCA && (
            <ProductCarousel
              tabKey="ca"
              items={data}
              renderItem={(item) => (
                <div style={{display: 'flex'}} className="Product_grid">
                  <img src={item.logo} alt="logo" className="Product_logo" />
                  <Flex
                    style={{padding: '0px 15px 25px 0px', height: '100%'}}
                    vertical
                  >
                    <h3 className="prodcardTiltle">{item.title}</h3>
                    <p className="productDecText">{item.description}</p>
                    {!item.isBlock ? (
                      <Button
                        className="actionBtn"
                        onClick={() => {
                          setProduct(item?.product ?? '');
                          localStorage.setItem('product', item?.product ?? '');
                          navigate(item.redirect ?? '/');
                          setOpenKeys(
                            item.mainRoute
                              ? Array.isArray(item.mainRoute)
                                ? item.mainRoute
                                : [item.mainRoute]
                              : []
                          );
                        }}
                        style={{
                          marginTop: 'auto',
                          width: 'fit-content',
                          maxWidth: '150px'
                        }}
                      >
                        Add Data
                      </Button>
                    ) : (
                      <UpgradeButton
                        title="Add Data"
                        style={{
                          marginTop: 'auto',
                          width: 'fit-content',
                          maxWidth: '150px'
                        }}
                      />
                    )}
                  </Flex>
                </div>
              )}
            />
          )}

          {selectedESG && (
            <ProductCarousel
              tabKey="esg"
              items={ESGdata}
              renderItem={(item) => (
                <div className="Product_grid">
                  <img src={item.logo} alt="logo" className="Product_logo" />
                  <Flex
                    style={{padding: '0px 15px 25px 0px', height: '100%'}}
                    vertical
                  >
                    <h3 className="prodcardTiltle">{item.title}</h3>
                    <p className="productDecText">{item.description}</p>
                    {!item.isBlock ? (
                      <Button
                        className="actionBtn"
                        onClick={() => {
                          setProduct(item?.product ?? '');
                          localStorage.setItem('product', item?.product ?? '');
                          navigate(item.redirect ?? '/');
                        }}
                        disabled={!!item?.isUpcoming}
                        style={{
                          backgroundColor: item?.isUpcoming ? '#435b37' : '',
                          marginTop: 'auto',
                          width: 'fit-content',
                          maxWidth: '150px'
                        }}
                      >
                        {item?.isUpcoming ? 'Coming Soon...' : 'Add Data'}
                      </Button>
                    ) : (
                      <UpgradeButton
                        title={item?.isUpcoming ? 'Coming Soon' : 'Add Data'}
                        style={{
                          marginTop: 'auto',
                          width: 'fit-content',
                          maxWidth: '150px'
                        }}
                      />
                    )}
                  </Flex>
                </div>
              )}
            />
          )}

          {selectedStrategy && (
            <ProductCarousel
              tabKey="strategy"
              items={strategyData}
              renderItem={(item) => (
                <div className="Product_grid">
                  <img src={item.logo} alt="logo" className="Product_logo" />
                  <Flex
                    style={{padding: '0px 15px 25px 0px', height: '100%'}}
                    vertical
                  >
                    <h3 className="prodcardTiltle">{item.title}</h3>
                    <p className="productDecText">{item.description}</p>
                    {!item.isBlock ? (
                      <a
                        href={item.href}
                        style={{
                          marginTop: 'auto'
                        }}
                      >
                        <Button
                          className="actionBtn"
                          onClick={() => {
                            setProduct(item?.product ?? '');
                            localStorage.setItem(
                              'product',
                              item?.product ?? ''
                            );
                          }}
                          style={{
                            marginTop: 'auto',
                            width: 'fit-content',
                            maxWidth: '150px'
                          }}
                        >
                          Add Data
                        </Button>
                      </a>
                    ) : (
                      <UpgradeButton
                        title="Add Data"
                        style={{
                          marginTop: 'auto',
                          width: 'fit-content',
                          maxWidth: '150px'
                        }}
                      />
                    )}
                  </Flex>
                </div>
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductSection;
